import React, { useState } from 'react';
import { baseurl } from '../../baseurl';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400&family=Fraunces:ital,wght@0,700;0,800;1,700;1,800&display=swap');

  :root {
    --tomato:     #ff4d2e;
    --tomato-d:   #e03a1c;
    --saffron:    #ff9f1c;
    --mint:       #00c896;
    --mint-d:     #00a87e;
    --sky:        #e8f7ff;
    --cream:      #fff9f5;
    --surface:    #ffffff;
    --ink:        #1a0f0a;
    --text:       #2d1a12;
    --muted:      #8a6f65;
    --border:     rgba(255,77,46,0.12);
    --border-s:   rgba(255,77,46,0.28);
    --shadow-r:   rgba(255,77,46,0.18);
    --shadow-m:   rgba(0,200,150,0.15);
  }

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .pos-root {
    min-height: 100vh;
    background: var(--cream);
    display: flex;
    align-items: stretch;
    font-family: 'Plus Jakarta Sans', sans-serif;
    overflow: hidden;
  }

  /* ═══════════════════════════════════════
     LEFT PANEL — vivid gradient visual
  ═══════════════════════════════════════ */
  .pos-visual {
    flex: 0 0 48%;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    /* Modern vibrant gradient */
    background: linear-gradient(145deg, #ff4d2e 0%, #ff7f2a 35%, #ff9f1c 65%, #ffbe3d 100%);
  }

  /* Mesh overlay for depth */
  .pos-visual::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse at 80% 20%, rgba(255,255,255,0.18) 0%, transparent 45%),
      radial-gradient(ellipse at 15% 85%, rgba(255,50,0,0.35) 0%, transparent 50%);
    z-index: 1;
  }

  /* Diagonal stripe texture */
  .pos-visual::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image: repeating-linear-gradient(
      -45deg,
      transparent,
      transparent 28px,
      rgba(255,255,255,0.04) 28px,
      rgba(255,255,255,0.04) 30px
    );
    z-index: 1;
  }

  /* Abstract circle blobs */
  .pos-blob {
    position: absolute;
    border-radius: 50%;
    z-index: 1;
  }
  .pos-blob-1 {
    width: 420px; height: 420px;
    top: -100px; right: -120px;
    background: rgba(255,255,255,0.10);
    border: 1px solid rgba(255,255,255,0.15);
  }
  .pos-blob-2 {
    width: 280px; height: 280px;
    bottom: 40px; left: -80px;
    background: rgba(255,255,255,0.08);
    border: 1px solid rgba(255,255,255,0.12);
  }
  .pos-blob-3 {
    width: 160px; height: 160px;
    top: 45%; left: 55%;
    background: rgba(255,255,255,0.06);
  }

  /* Floating food emojis */
  .pos-food-grid { position: absolute; inset: 0; z-index: 2; overflow: hidden; }

  .pos-food-icon {
    position: absolute;
    font-size: 72px;
    opacity: 0.13;
    animation: pos-float linear infinite;
    user-select: none;
    filter: drop-shadow(0 4px 12px rgba(0,0,0,0.15));
  }
  .pos-food-icon:nth-child(1) { top: 4%;  left: 6%;  font-size: 56px; animation-duration: 18s; animation-delay: 0s; }
  .pos-food-icon:nth-child(2) { top: 12%; left: 52%; font-size: 84px; animation-duration: 22s; animation-delay: -5s; }
  .pos-food-icon:nth-child(3) { top: 32%; left: 18%; font-size: 66px; animation-duration: 20s; animation-delay: -9s; }
  .pos-food-icon:nth-child(4) { top: 48%; left: 68%; font-size: 50px; animation-duration: 25s; animation-delay: -2s; }
  .pos-food-icon:nth-child(5) { top: 62%; left: 8%;  font-size: 80px; animation-duration: 19s; animation-delay: -7s; }
  .pos-food-icon:nth-child(6) { top: 74%; left: 42%; font-size: 60px; animation-duration: 23s; animation-delay: -12s; }
  .pos-food-icon:nth-child(7) { top: 83%; left: 76%; font-size: 70px; animation-duration: 17s; animation-delay: -4s; }
  .pos-food-icon:nth-child(8) { top: 22%; left: 78%; font-size: 46px; animation-duration: 21s; animation-delay: -10s; }

  @keyframes pos-float {
    0%   { transform: translateY(0) rotate(0deg); }
    33%  { transform: translateY(-20px) rotate(5deg); }
    66%  { transform: translateY(10px) rotate(-4deg); }
    100% { transform: translateY(0) rotate(0deg); }
  }

  /* Spinning plate ornament */
  .pos-plate-wrap {
    position: absolute;
    top: 50%; left: 50%;
    transform: translate(-50%, -54%);
    width: 320px; height: 320px;
    z-index: 3;
    animation: pos-spin-slow 60s linear infinite;
  }

  @keyframes pos-spin-slow {
    to { transform: translate(-50%, -54%) rotate(360deg); }
  }

  .pos-center-emblem {
    position: absolute;
    top: 50%; left: 50%;
    transform: translate(-50%, -54%);
    width: 140px; height: 140px;
    border-radius: 50%;
    background: rgba(255,255,255,0.15);
    backdrop-filter: blur(8px);
    border: 1.5px solid rgba(255,255,255,0.3);
    display: flex; align-items: center; justify-content: center;
    font-size: 64px;
    z-index: 4;
    animation: pos-pulse 4s ease-in-out infinite;
    box-shadow: 0 8px 40px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.4);
  }

  @keyframes pos-pulse {
    0%, 100% { transform: translate(-50%, -54%) scale(1); }
    50%       { transform: translate(-50%, -54%) scale(1.05); }
  }

  /* Visual bottom content */
  .pos-visual-content {
    position: relative;
    z-index: 5;
    padding: 0 44px 48px;
    margin-top: auto;
  }

  .pos-tagline {
    font-family: 'Fraunces', serif;
    font-size: 46px;
    font-weight: 800;
    line-height: 1.05;
    color: #ffffff;
    letter-spacing: -1px;
    margin-bottom: 14px;
    text-shadow: 0 2px 20px rgba(0,0,0,0.12);
  }

  .pos-tagline em {
    font-style: italic;
    color: rgba(255,255,255,0.88);
    text-decoration: underline;
    text-decoration-color: rgba(255,255,255,0.35);
    text-underline-offset: 5px;
  }

  .pos-sub {
    color: rgba(255,255,255,0.72);
    font-size: 14px;
    font-weight: 400;
    line-height: 1.65;
    margin-bottom: 28px;
    max-width: 310px;
  }

  .pos-badges { display: flex; gap: 8px; flex-wrap: wrap; }

  .pos-badge {
    display: inline-flex; align-items: center; gap: 6px;
    background: rgba(255,255,255,0.18);
    border: 1px solid rgba(255,255,255,0.30);
    backdrop-filter: blur(8px);
    border-radius: 100px;
    padding: 6px 14px;
    font-size: 12px; font-weight: 600;
    color: #ffffff;
    letter-spacing: 0.2px;
  }

  .pos-badge-dot {
    width: 6px; height: 6px; border-radius: 50%;
    background: #ffffff;
    box-shadow: 0 0 8px rgba(255,255,255,0.9);
    animation: pos-blink 2s ease-in-out infinite;
  }

  @keyframes pos-blink { 0%,100%{opacity:1} 50%{opacity:0.2} }

  /* Top logo strip on visual */
  .pos-visual-top {
    position: relative;
    z-index: 5;
    padding: 32px 44px 0;
  }

  .pos-visual-logo {
    display: flex; align-items: center; gap: 10px;
  }

  .pos-visual-logo-icon {
    width: 36px; height: 36px; border-radius: 9px;
    background: rgba(255,255,255,0.2);
    border: 1px solid rgba(255,255,255,0.35);
    display: flex; align-items: center; justify-content: center;
    font-size: 18px;
    backdrop-filter: blur(8px);
  }

  .pos-visual-logo-text {
    font-family: 'Fraunces', serif;
    font-size: 18px; font-weight: 700;
    color: #fff;
    letter-spacing: 0.2px;
  }

  /* ═══════════════════════════════════════
     RIGHT PANEL — clean bright light form
  ═══════════════════════════════════════ */
  .pos-form-panel {
    flex: 1;
    background: var(--surface);
    display: flex; flex-direction: column; justify-content: center;
    padding: 64px 58px;
    position: relative;
    overflow: hidden;
  }

  /* Soft color wash top-right */
  .pos-form-panel::before {
    content: '';
    position: absolute;
    top: -60px; right: -60px;
    width: 280px; height: 280px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255,159,28,0.08) 0%, transparent 70%);
    pointer-events: none;
  }

  /* Mint accent bottom-left */
  .pos-form-panel::after {
    content: '';
    position: absolute;
    bottom: -50px; left: -50px;
    width: 220px; height: 220px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(0,200,150,0.07) 0%, transparent 70%);
    pointer-events: none;
  }

  /* Thin top border accent */
  .pos-top-accent {
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 4px;
    background: linear-gradient(90deg, var(--tomato), var(--saffron), var(--mint), var(--saffron), var(--tomato));
    background-size: 250% 100%;
    animation: pos-slide-bar 4s linear infinite;
  }

  @keyframes pos-slide-bar {
    0%   { background-position: 0% 0%; }
    100% { background-position: 250% 0%; }
  }

  /* Brand */
  .pos-brand {
    display: flex; align-items: center; gap: 12px;
    margin-bottom: 44px;
    position: relative; z-index: 1;
  }

  .pos-brand-icon {
    width: 44px; height: 44px; border-radius: 12px;
    background: linear-gradient(135deg, var(--tomato), var(--saffron));
    display: flex; align-items: center; justify-content: center;
    font-size: 22px;
    box-shadow: 0 6px 20px var(--shadow-r), 0 2px 6px rgba(0,0,0,0.08);
    flex-shrink: 0;
  }

  .pos-brand-name {
    font-family: 'Fraunces', serif;
    font-size: 20px; font-weight: 700;
    color: var(--ink);
  }

  .pos-brand-ver {
    font-size: 11px; font-weight: 500;
    color: var(--muted);
    letter-spacing: 1px; text-transform: uppercase;
    margin-top: 1px;
  }

  /* Heading */
  .pos-heading {
    position: relative; z-index: 1;
    margin-bottom: 32px;
  }

  .pos-eyebrow {
    display: flex; align-items: center; gap: 8px;
    font-size: 11px; font-weight: 700;
    letter-spacing: 2.5px; text-transform: uppercase;
    color: var(--tomato);
    margin-bottom: 10px;
  }

  .pos-eyebrow-pill {
    display: inline-block;
    background: rgba(255,77,46,0.10);
    border: 1px solid rgba(255,77,46,0.20);
    border-radius: 100px;
    padding: 3px 10px;
    font-size: 10px; font-weight: 700;
    letter-spacing: 1.5px;
    color: var(--tomato);
  }

  .pos-h1 {
    font-family: 'Fraunces', serif;
    font-size: 40px; font-weight: 800;
    color: var(--ink);
    line-height: 1.08;
    letter-spacing: -0.8px;
  }

  .pos-h1-sub {
    color: var(--muted);
    font-size: 14px; font-weight: 400;
    margin-top: 10px; line-height: 1.55;
  }

  /* Colorful stat pills below heading */
  .pos-stat-row {
    display: flex; gap: 8px; margin-top: 18px; flex-wrap: wrap;
  }

  .pos-stat {
    display: inline-flex; align-items: center; gap: 6px;
    border-radius: 10px;
    padding: 7px 12px;
    font-size: 12px; font-weight: 600;
  }

  .pos-stat-r {
    background: rgba(255,77,46,0.08);
    color: var(--tomato-d);
    border: 1px solid rgba(255,77,46,0.15);
  }

  .pos-stat-g {
    background: rgba(0,200,150,0.09);
    color: var(--mint-d);
    border: 1px solid rgba(0,200,150,0.18);
  }

  .pos-stat-o {
    background: rgba(255,159,28,0.10);
    color: #c47800;
    border: 1px solid rgba(255,159,28,0.20);
  }

  /* Fields */
  .pos-field { position: relative; z-index: 1; margin-bottom: 16px; }

  .pos-label {
    font-size: 11px; font-weight: 700;
    letter-spacing: 1.5px; text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 8px;
    display: flex; align-items: center; gap: 6px;
  }

  .pos-input-shell { position: relative; }

  .pos-input {
    width: 100%;
    padding: 14px 18px 14px 48px;
    background: #fafafa;
    border: 1.5px solid #ede8e2;
    border-radius: 14px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 15px; font-weight: 400;
    color: var(--ink);
    outline: none;
    transition: all 0.22s;
    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  }

  .pos-input::placeholder { color: #c8bdb5; }

  .pos-input:focus {
    border-color: var(--tomato);
    background: #fff;
    box-shadow: 0 0 0 4px rgba(255,77,46,0.09), 0 2px 8px rgba(0,0,0,0.05);
  }

  .pos-input-icon {
    position: absolute; left: 16px; top: 50%;
    transform: translateY(-50%);
    color: #c8bdb5;
    display: flex; align-items: center;
    pointer-events: none;
    transition: color 0.22s;
  }

  .pos-input-shell:focus-within .pos-input-icon { color: var(--tomato); }

  /* Animated bottom underline */
  .pos-focus-bar {
    position: absolute;
    bottom: 1px; left: 14px; right: 14px;
    height: 2px;
    border-radius: 2px;
    background: linear-gradient(90deg, var(--tomato), var(--saffron));
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s cubic-bezier(.22,.8,.44,1);
  }

  .pos-input-shell:focus-within .pos-focus-bar { transform: scaleX(1); }

  /* Submit */
  .pos-submit-wrap { position: relative; z-index: 1; margin-top: 12px; margin-bottom: 24px; }

  .pos-btn {
    width: 100%;
    padding: 16px;
    background: linear-gradient(135deg, var(--tomato) 0%, #f0581a 45%, var(--saffron) 100%);
    border: none; border-radius: 14px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 15px; font-weight: 700;
    color: #fff; cursor: pointer;
    letter-spacing: 0.3px;
    position: relative; overflow: hidden;
    transition: transform 0.15s, box-shadow 0.2s, filter 0.15s;
    box-shadow: 0 8px 28px var(--shadow-r), 0 2px 8px rgba(0,0,0,0.1),
                inset 0 1px 0 rgba(255,255,255,0.18);
  }

  /* Shimmer sweep */
  .pos-btn::before {
    content: '';
    position: absolute;
    top: 0; left: -80%;
    width: 60%; height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent);
    transform: skewX(-18deg);
    transition: left 0.55s;
  }
  .pos-btn:hover::before { left: 140%; }

  .pos-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 14px 36px rgba(255,77,46,0.38), 0 4px 12px rgba(0,0,0,0.12);
    filter: brightness(1.05);
  }
  .pos-btn:active:not(:disabled) { transform: translateY(0); }
  .pos-btn:disabled { opacity: 0.6; cursor: not-allowed; }

  .pos-btn-inner {
    display: flex; align-items: center; justify-content: center;
    gap: 10px; position: relative; z-index: 1;
  }

  .pos-btn-arrow { display: inline-flex; transition: transform 0.2s; }
  .pos-btn:hover:not(:disabled) .pos-btn-arrow { transform: translateX(5px); }

  .pos-spinner {
    width: 17px; height: 17px;
    border: 2px solid rgba(255,255,255,0.3);
    border-top-color: #fff;
    border-radius: 50%;
    animation: pos-spin 0.65s linear infinite;
  }

  @keyframes pos-spin { to { transform: rotate(360deg); } }

  /* Divider */
  .pos-or-divider {
    display: flex; align-items: center; gap: 12px;
    margin-bottom: 16px; position: relative; z-index: 1;
  }

  .pos-or-divider::before,
  .pos-or-divider::after {
    content: ''; flex: 1; height: 1px;
    background: #ede8e2;
  }

  .pos-or-text {
    font-size: 11px; font-weight: 600;
    color: #c8bdb5; letter-spacing: 1px;
    text-transform: uppercase;
  }

  /* Footer */
  .pos-footer {
    position: relative; z-index: 1;
    display: flex; justify-content: space-between; align-items: center;
  }

  .pos-link {
    font-size: 13px; font-weight: 500;
    color: var(--muted); text-decoration: none;
    transition: color 0.2s;
    display: flex; align-items: center; gap: 5px;
  }

  .pos-link:hover { color: var(--tomato); }

  .pos-footer-sep { width: 4px; height: 4px; border-radius: 50%; background: #ddd5cc; }

  /* Mint "secure" pill at bottom */
  .pos-secure-note {
    display: flex; align-items: center; justify-content: center; gap: 6px;
    margin-top: 20px;
    font-size: 11px; font-weight: 500;
    color: var(--mint-d);
    position: relative; z-index: 1;
  }

  .pos-secure-note svg { opacity: 0.8; }

  /* Animations */
  .pos-animate { animation: pos-in 0.5s cubic-bezier(.22,.8,.44,1) both; }
  .pos-animate-1 { animation-delay: 0.04s; }
  .pos-animate-2 { animation-delay: 0.10s; }
  .pos-animate-3 { animation-delay: 0.17s; }
  .pos-animate-4 { animation-delay: 0.24s; }
  .pos-animate-5 { animation-delay: 0.31s; }
  .pos-animate-6 { animation-delay: 0.38s; }

  @keyframes pos-in {
    from { opacity: 0; transform: translateY(18px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* ── Responsive ── */
  @media (max-width: 960px) {
    .pos-visual { flex: 0 0 44%; }
    .pos-visual-content { padding: 0 36px 40px; }
    .pos-tagline { font-size: 38px; }
    .pos-form-panel { padding: 52px 44px; }
    .pos-h1 { font-size: 34px; }
  }

  @media (max-width: 680px) {
    .pos-root { flex-direction: column; }
    .pos-visual { flex: none; height: 220px; }
    .pos-plate-wrap { width: 170px; height: 170px; }
    .pos-center-emblem { width: 80px; height: 80px; font-size: 36px; }
    .pos-visual-content { padding: 0 24px 22px; }
    .pos-tagline { font-size: 26px; margin-bottom: 0; }
    .pos-sub, .pos-badges { display: none; }
    .pos-form-panel { padding: 36px 28px 40px; }
    .pos-brand { margin-bottom: 28px; }
    .pos-heading { margin-bottom: 22px; }
    .pos-h1 { font-size: 30px; }
    .pos-stat-row { display: none; }
  }

  @media (max-width: 400px) {
    .pos-form-panel { padding: 28px 20px 32px; }
    .pos-h1 { font-size: 26px; }
  }
`;

function Login() {
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [loading,  setLoading]  = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${baseurl}/loginRestaurant`, { email, password });
      if (response.data.success) {
        localStorage.setItem('restaurant_token', response.data.token);
        Swal.fire({
          icon: 'success',
          title: 'Login Successful',
          text: 'Initializing terminal...',
          showConfirmButton: false,
          timer: 1500,
        });
        setTimeout(() => navigate('/dashboard'), 1500);
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Login Failed',
        text: error.response?.data?.message || 'Invalid credentials',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{styles}</style>
      <div className="pos-root">

        {/* ══ LEFT — Vibrant gradient visual panel ══ */}
        <div className="pos-visual">
          <div className="pos-blob pos-blob-1" />
          <div className="pos-blob pos-blob-2" />
          <div className="pos-blob pos-blob-3" />

          <div className="pos-food-grid">
            {['🍕','🍔','🥩','🍷','🧑‍🍳','🥗','🍜','🫕'].map((e, i) => (
              <span key={i} className="pos-food-icon">{e}</span>
            ))}
          </div>

          {/* Ornamental ring */}
          <svg className="pos-plate-wrap" viewBox="0 0 320 320" fill="none">
            <circle cx="160" cy="160" r="156" stroke="rgba(255,255,255,0.22)" strokeWidth="1" strokeDasharray="6 10"/>
            <circle cx="160" cy="160" r="132" stroke="rgba(255,255,255,0.12)" strokeWidth="1"/>
            <circle cx="160" cy="160" r="108" stroke="rgba(255,255,255,0.08)" strokeWidth="1" strokeDasharray="3 5"/>
            {Array.from({length: 24}).map((_, i) => {
              const a = (i * 15 * Math.PI) / 180;
              return (
                <line key={i}
                  x1={160 + 150 * Math.cos(a)} y1={160 + 150 * Math.sin(a)}
                  x2={160 + 140 * Math.cos(a)} y2={160 + 140 * Math.sin(a)}
                  stroke="rgba(255,255,255,0.30)" strokeWidth="1.2"
                />
              );
            })}
          </svg>

          <div className="pos-center-emblem">🍽️</div>

          {/* Top logo on visual */}
          <div className="pos-visual-top">
            <div className="pos-visual-logo">
              <div className="pos-visual-logo-icon">🔥</div>
              <span className="pos-visual-logo-text">Restrowave</span>
            </div>
          </div>

          <div className="pos-visual-content">
            <h2 className="pos-tagline">
              Every Order,<br /><em>Perfectly Served.</em>
            </h2>
            <p className="pos-sub">
              Your all-in-one command centre for seamless restaurant operations — tables, orders, analytics.
            </p>
            <div className="pos-badges">
              <span className="pos-badge"><span className="pos-badge-dot" />Live Orders</span>
              <span className="pos-badge"><span className="pos-badge-dot" />Table Management</span>
              <span className="pos-badge"><span className="pos-badge-dot" />Analytics</span>
            </div>
          </div>
        </div>

        {/* ══ RIGHT — Clean white form panel ══ */}
        <div className="pos-form-panel">
          <div className="pos-top-accent" />

          {/* Brand */}
          <div className="pos-brand pos-animate pos-animate-1">
            <div className="pos-brand-icon">🔥</div>
            <div>
              <div className="pos-brand-name">Restrowave POS</div>
              <div className="pos-brand-ver">v2.0 · Terminal</div>
            </div>
          </div>

          {/* Heading */}
          <div className="pos-heading pos-animate pos-animate-2">
            <div className="pos-eyebrow">
              <span className="pos-eyebrow-pill">STAFF ACCESS</span>
            </div>
            <h1 className="pos-h1">Welcome Back,<br />Chef. 👋</h1>
            <p className="pos-h1-sub">Sign in to initialize your POS terminal</p>

            {/* Colorful stat pills */}
            {/* <div className="pos-stat-row">
              <span className="pos-stat pos-stat-r">
                <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor"><circle cx="5" cy="5" r="5"/></svg>
                12 Tables Active
              </span>
              <span className="pos-stat pos-stat-g">
                <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor"><circle cx="5" cy="5" r="5"/></svg>
                Kitchen Online
              </span>
              <span className="pos-stat pos-stat-o">
                <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor"><circle cx="5" cy="5" r="5"/></svg>
                ₹24,860 Today
              </span>
            </div> */}
          </div>

          {/* Form */}
          <form onSubmit={handleLogin}>
            <div className="pos-field pos-animate pos-animate-3">
              <label className="pos-label">Email</label>
              <div className="pos-input-shell">
                <span className="pos-input-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <rect x="2" y="4" width="20" height="16" rx="2"/>
                    <path d="M22 7l-10 7L2 7"/>
                  </svg>
                </span>
                <input
                  className="pos-input"
                  type="email"
                  placeholder="chef@restrowave.com"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
                <div className="pos-focus-bar" />
              </div>
            </div>

            <div className="pos-field pos-animate pos-animate-4">
              <label className="pos-label">Password / PIN</label>
              <div className="pos-input-shell">
                <span className="pos-input-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <rect x="3" y="11" width="18" height="11" rx="2"/>
                    <path d="M7 11V7a5 5 0 0110 0v4"/>
                  </svg>
                </span>
                <input
                  className="pos-input"
                  type="password"
                  placeholder="••••••••"
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
                <div className="pos-focus-bar" />
              </div>
            </div>

            <div className="pos-submit-wrap pos-animate pos-animate-5">
              <button type="submit" className="pos-btn" disabled={loading}>
                <span className="pos-btn-inner">
                  {loading ? (
                    <><span className="pos-spinner" /> Authenticating…</>
                  ) : (
                    <>
                      Initialize Terminal
                      <span className="pos-btn-arrow">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                          <path d="M5 12h14M13 6l6 6-6 6"/>
                        </svg>
                      </span>
                    </>
                  )}
                </span>
              </button>
            </div>

            <div className="pos-footer pos-animate pos-animate-6">
              <a href="#" className="pos-link">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/>
                </svg>
                Help Center
              </a>
              <span className="pos-footer-sep" />
              <a href="#" className="pos-link">Forgot PIN?</a>
            </div>
          </form>

          {/* Secure note */}
          <div className="pos-secure-note pos-animate pos-animate-6">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            256-bit encrypted · Secure terminal session
          </div>
        </div>

      </div>
    </>
  );
}

export default Login;
